import { __assign, __makeTemplateObject, __rest } from "tslib";
import * as React from 'react';
import * as PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { border, spacing } from '@metro-ui/tokens/js';
import { ActionButton, IconButton } from '../Button';
import { Plus } from '../icons/icons/Plus';
import { Minus } from '../icons/icons/Minus';
import { InputElement } from '../Input/components/Element';
import { fontStack } from '../utils/fontStack';
import { increment, decrement, setInputValue } from './AmountPicker.utils';
import { arrowsHidden } from '../utils/cssUtils';
import { borderAnimation, statusStyle } from '../Input/util';
export var AmountPicker = styled(React.forwardRef(function (_a, ref) {
    var id = _a.id, _b = _a.min, min = _b === void 0 ? 0 : _b, // default minimum value
    max = _a.max, value = _a.value, isReduced = _a.isReduced, isDisabled = _a.isDisabled, isReadOnly = _a.isReadOnly, onChange = _a.onChange, label = _a.label, className = _a.className, htmlAttributes = __rest(_a, ["id", "min", "max", "value", "isReduced", "isDisabled", "isReadOnly", "onChange", "label", "className"]);
    var _c = React.useState(value), count = _c[0], setCount = _c[1];
    // when value comes from parent, update state
    React.useEffect(function () {
        setCount(value);
    }, [value]);
    var setState = function (v) {
        setCount(v);
        if (onChange && v !== count) {
            onChange(v);
        }
    };
    var isBelowMin = function (num, minValue) {
        return typeof num === 'number' && num <= minValue;
    };
    var isAboveMax = function (num, maxValue) {
        return typeof num === 'number' &&
            typeof maxValue === 'number' &&
            num >= maxValue;
    };
    var minusProps = {
        label: 'decrement-control',
        Icon: Minus,
        isDisabled: isBelowMin(count, min) || isDisabled || isReadOnly,
        onClick: function () { return setState(decrement(count, min)); },
        className: 'mds-amountpicker-minus-btn',
    };
    var plusProps = {
        label: 'increment-control',
        Icon: Plus,
        isDisabled: isAboveMax(count, max) || isDisabled || isReadOnly,
        onClick: function () { return setState(increment(count, max)); },
        className: 'mds-amountpicker-plus-btn',
    };
    return (React.createElement("div", { className: className },
        isReduced ? (React.createElement(IconButton, __assign({}, minusProps, { size: "small", hoverBackgroundColor: 'blue-tint-95', hoverColor: 'blue' }))) : (React.createElement(ActionButton, __assign({ variant: "secondary" }, minusProps))),
        React.createElement(InputElement, __assign({ id: id, type: "number", inputMode: "numeric", pattern: "[0-9]*", readOnly: isReadOnly, disabled: isDisabled || isReadOnly }, htmlAttributes, { value: setInputValue(count), ref: ref, onChange: function (e) {
                return setState(e.currentTarget.value
                    ? e.currentTarget.valueAsNumber
                    : undefined);
            }, onKeyPress: function (e) {
                if (['e', 'E'].includes(e.key)) {
                    e.preventDefault();
                }
            }, "aria-label": label, className: "mds-amountpicker-field" })),
        isReduced ? (React.createElement(IconButton, __assign({ size: "small", hoverBackgroundColor: 'blue-tint-95', hoverColor: 'blue' }, plusProps))) : (React.createElement(ActionButton, __assign({ variant: "secondary" }, plusProps)))));
}))(function (_a) {
    var isReduced = _a.isReduced, isValid = _a.isValid, hasInfo = _a.hasInfo, hasError = _a.hasError;
    var padding = isReduced ? spacing['xx-small'] : '';
    var outerButtonPadding = isReduced ? spacing['xxx-small'] : 0;
    return css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    ", "\n    position: relative;\n    display: flex;\n    align-items: center;\n\n    &::after {\n      pointer-events: none;\n      position: absolute;\n      content: ' ';\n      width: 100%;\n      height: 100%;\n      box-sizing: border-box;\n      border: ", " solid transparent;\n      border-radius: ", ";\n\n      ", "\n\n      ", "\n      ", "\n      ", "\n    }\n\n    &:focus-within {\n      &::after {\n        ", "\n      }\n    }\n\n    .mds-amountpicker-minus-btn {\n      position: absolute;\n      left: ", ";\n      border-radius: ", " 0 0 ", ";\n      padding: ", ";\n    }\n\n    .mds-amountpicker-field {\n      text-align: center;\n      height: ", ";\n      ", "\n      ", "\n    }\n\n    .mds-amountpicker-plus-btn {\n      position: absolute;\n      right: ", ";\n      border-radius: 0 ", " ", " 0;\n      padding: ", ";\n    }\n  "], ["\n    ", "\n    position: relative;\n    display: flex;\n    align-items: center;\n\n    &::after {\n      pointer-events: none;\n      position: absolute;\n      content: ' ';\n      width: 100%;\n      height: 100%;\n      box-sizing: border-box;\n      border: ", " solid transparent;\n      border-radius: ", ";\n\n      ", "\n\n      ", "\n      ", "\n      ", "\n    }\n\n    &:focus-within {\n      &::after {\n        ", "\n      }\n    }\n\n    .mds-amountpicker-minus-btn {\n      position: absolute;\n      left: ", ";\n      border-radius: ", " 0 0 ", ";\n      padding: ", ";\n    }\n\n    .mds-amountpicker-field {\n      text-align: center;\n      height: ", ";\n      ", "\n      ", "\n    }\n\n    .mds-amountpicker-plus-btn {\n      position: absolute;\n      right: ", ";\n      border-radius: 0 ", " ", " 0;\n      padding: ", ";\n    }\n  "])), fontStack, border.width.thin, border.radius.small, borderAnimation, isValid && statusStyle('valid'), hasInfo && statusStyle('info'), hasError && statusStyle('error'), statusStyle('focused'), outerButtonPadding, border.radius.small, border.radius.small, padding, isReduced ? '28px' : '', arrowsHidden, isReduced && css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n        box-shadow: none;\n      "], ["\n        box-shadow: none;\n      "]))), outerButtonPadding, border.radius.small, border.radius.small, padding);
});
AmountPicker.propTypes = {
    id: PropTypes.string.isRequired,
    min: PropTypes.number,
    max: PropTypes.number,
    value: PropTypes.number,
    isReduced: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isReadOnly: PropTypes.bool,
    hasError: PropTypes.bool,
    hasInfo: PropTypes.bool,
    isValid: PropTypes.bool,
    onChange: PropTypes.func,
    className: PropTypes.string,
};
var templateObject_1, templateObject_2;
//# sourceMappingURL=AmountPicker.js.map